<template>
  <!--
    AB#21807 — the Comms mood drawer. One instance for the app (the header mounts
    it); the badge, the Needs Attention chip and the team grid open it through
    `drawer` in useCommsMood. Built on the shared Drawer shell, so it sits below
    the app bar; there is no action footer, so the scroll body carries the
    bottom clearance for the Ask Atlas launcher itself.
  -->
  <Drawer form
    v-if="drawer.open"
    :title="heading"
    :subtitle="subtitle"
    :width="isTeam ? '780px' : '480px'"
    data-test="comms-mood-drawer"
    @close="closeMoodDrawer"
  >
    <template v-if="!isTeam" #subhead>
      <button
        v-if="drawer.fromTeam"
        type="button"
        class="cmd-back"
        data-test="cmd-back-to-team"
        @click="openTeamMoodDrawer"
      >
        <ArrowLeftIcon class="cmd-back-ic" aria-hidden="true" />Team comms mood
      </button>
      <div class="cmd-tabs" role="tablist" aria-label="Period">
        <button
          v-for="t in TABS"
          :key="t.key"
          type="button"
          role="tab"
          class="cmd-tab"
          :class="{ on: drawer.period === t.key }"
          :aria-selected="drawer.period === t.key"
          :data-test="`cmd-tab-${t.key}`"
          :title="t.hint"
          @click="drawer.period = t.key"
        >
          <MoodFace v-if="summary" :level="faceFor(t.key)" :size="18" />
          {{ t.label }}
          <span v-if="t.hint" class="cmd-tab-hint">{{ t.hint }}</span>
        </button>
      </div>
    </template>

    <!-- The team grid (view-team-communication-mood) -->
    <div v-if="isTeam" class="cmd-body" data-test="cmd-team">
      <div v-if="teamLoading && !team" class="cmd-state">Reading the team's conversations…</div>
      <div v-else-if="!team" class="cmd-state" data-test="cmd-team-unavailable">
        The team comms mood isn't available right now.
      </div>
      <template v-else>
        <div class="cmd-team-faces">
          <div v-for="p in teamTrio" :key="p.key" class="cmd-team-face">
            <MoodFace :level="p.face.level" :size="34" />
            <span
              ><b>{{ p.label }}</b> {{ p.face.label }}</span
            >
          </div>
        </div>
        <TeamMoodGrid :team="team" @open="(p) => openPersonFromTeam(p.user_id, p.name)" />
        <h3 class="cmd-h">What the faces mean</h3>
        <ul class="cmd-legend" data-test="cmd-legend">
          <li v-for="l in team.legend" :key="l.level">
            <MoodFace :level="l.level" :size="22" />
            <span
              ><b>{{ l.label }}</b> — {{ l.meaning }}</span
            >
          </li>
        </ul>
      </template>
    </div>

    <div v-else class="cmd-body">
      <div v-if="loading && !summary" class="cmd-state">Reading the conversations…</div>
      <div v-else-if="!summary" class="cmd-state" data-test="cmd-unavailable">
        The comms mood isn't available right now.
      </div>

      <template v-else>
        <!-- The headline -->
        <div class="cmd-hero" :class="`cmd-hero--l${detail.level}`" data-test="cmd-hero">
          <MoodFace :level="detail.level" :size="52" />
          <div class="cmd-hero-t">
            <b>{{ headline }}</b>
            <span>{{ countsLine }}</span>
          </div>
        </div>

        <!-- Four dimensions -->
        <h3 class="cmd-h">How it breaks down</h3>
        <div class="cmd-dims">
          <div v-for="d in dims" :key="d.key" class="cmd-dim" :data-test="`cmd-dim-${d.key}`">
            <MoodFace :level="d.face ? d.face.level : 0" :size="30" />
            <div>
              <b>{{ d.label }}</b>
              <small>{{ d.note }}</small>
            </div>
          </div>
        </div>

        <!-- The moments behind it -->
        <h3 class="cmd-h">What drove it</h3>
        <p v-if="!reasons.length" class="cmd-muted">
          Nothing stood out{{ detail.level === 0 ? ' — no messages in this period' : '' }}.
        </p>
        <article
          v-for="(r, i) in reasons"
          :key="`${r.kind}-${r.message_id ?? i}`"
          class="cmd-ev"
          :class="{ 'cmd-ev--good': r.kind === 'good' }"
          data-test="cmd-reason"
        >
          <div class="cmd-ev-t">
            <MoodFace :level="severityFace(r.severity)" :size="20" />
            <span>{{ r.title }}</span>
          </div>
          <q v-if="r.quote" class="cmd-ev-q">{{ plainText(r.quote) }}</q>
          <div class="cmd-ev-m">
            <!-- Opens the order in a new tab (the dashboard reads its deep link
                 only on load, and the drawer stays open here). -->
            <a
              v-if="orderHref(r)"
              :href="orderHref(r)!"
              target="_blank"
              rel="noopener"
              class="cmd-ev-order"
              data-test="cmd-order-link"
              :title="`Open order #${r.order_no || r.order_id} in a new tab`"
              >#{{ r.order_no || r.order_id }}</a
            >
            <span v-else-if="r.order_no || r.order_id" class="cmd-ev-order-plain"
              >#{{ r.order_no || r.order_id }}</span
            >
            <span v-if="r.counterpart">{{ counterpartLabel(r) }}</span>
            <span v-if="r.channel">{{ CHANNEL_LABELS[r.channel] || r.channel }}</span>
            <span v-if="r.at">{{ when(r.at) }}</span>
          </div>
          <p v-if="r.why" class="cmd-ev-why">{{ plainText(r.why) }}</p>
          <p v-if="r.suggestion && r.kind !== 'good'" class="cmd-ev-sug">
            <LightBulbIcon class="cmd-ev-sug-ic" aria-hidden="true" />{{ plainText(r.suggestion) }}
          </p>

          <!-- "This is wrong" — only on your own judgements -->
          <div v-if="isOwn && r.message_id && r.kind !== 'good'" class="cmd-flag">
            <span v-if="flagged.has(r.message_id)" class="cmd-flag-done" data-test="cmd-flag-done">
              Thanks — flagged for review.
            </span>
            <template v-else-if="flagOpen === r.message_id">
              <label class="cmd-flag-lab" :for="`cmd-flag-${r.message_id}`"
                >What did it get wrong? (optional)</label
              >
              <textarea
                :id="`cmd-flag-${r.message_id}`"
                v-model="flagNote"
                class="cmd-flag-in"
                rows="2"
                maxlength="500"
              ></textarea>
              <div class="cmd-flag-row">
                <button type="button" class="dash-btn" @click="flagOpen = null">Cancel</button>
                <button
                  type="button"
                  class="dash-btn dash-btn--primary"
                  data-test="cmd-flag-send"
                  :disabled="flagBusy"
                  @click="sendFlag(r.message_id)"
                >
                  Flag it
                </button>
              </div>
            </template>
            <button
              v-else
              type="button"
              class="cmd-flag-btn"
              data-test="cmd-flag"
              @click="startFlag(r.message_id)"
            >
              This is wrong
            </button>
          </div>
        </article>

        <!-- Legend -->
        <h3 class="cmd-h">What the faces mean</h3>
        <ul class="cmd-legend" data-test="cmd-legend">
          <li v-for="l in summary.legend" :key="l.level">
            <MoodFace :level="l.level" :size="22" />
            <span
              ><b>{{ l.label }}</b> — {{ l.meaning }}</span
            >
          </li>
        </ul>

        <p class="cmd-foot">
          Read from {{ summary.sources.join(', ') }}. Only messages written to customers and
          factories are judged — not internal notes, and not reply speed.
          <template v-if="detail.computed_at"> Updated {{ when(detail.computed_at) }}.</template>
        </p>
      </template>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ArrowLeftIcon, LightBulbIcon } from '@heroicons/vue/24/outline';
import Drawer from '@/components/Shared/Drawer.vue';
import MoodFace from './MoodFace.vue';
import TeamMoodGrid from './TeamMoodGrid.vue';
import {
  CHANNEL_LABELS,
  closeMoodDrawer,
  drawer,
  fetchSummary,
  fetchTeam,
  openPersonFromTeam,
  openTeamMoodDrawer,
  plainText,
  type TeamMood,
  flagJudgement,
  type MoodLevel,
  type MoodPeriod,
  type MoodReason,
  type MoodSummary,
} from '@/composables/useCommsMood';

const TABS: { key: MoodPeriod; label: string; hint?: string }[] = [
  { key: 'day', label: 'Today' },
  // Rolling windows, so the drawer never empties on a Monday or on the 1st.
  { key: 'week', label: 'Week', hint: 'Last 7 days' },
  { key: 'month', label: 'Month', hint: 'Last 30 days' },
];

const summary = ref<MoodSummary | null>(null);
const loading = ref(false);

const isOwn = computed(() => drawer.subjectId === null);
const isTeam = computed(() => drawer.view === 'team');

const team = ref<TeamMood | null>(null);
const teamLoading = ref(false);
const loadTeam = async (): Promise<void> => {
  teamLoading.value = true;
  const r = await fetchTeam();
  team.value = r && r.enabled ? r : null;
  teamLoading.value = false;
};
const teamTrio = computed(() =>
  team.value
    ? [
        { key: 'today', label: 'Today', face: team.value.faces.today },
        { key: 'week', label: 'Week', face: team.value.faces.week },
        { key: 'month', label: 'Month', face: team.value.faces.month },
      ]
    : []
);

const load = async (): Promise<void> => {
  loading.value = true;
  const r = await fetchSummary(drawer.period, drawer.subjectId);
  summary.value = r && r.enabled ? r : null;
  loading.value = false;
};

watch(
  () => [drawer.open, drawer.view, drawer.period, drawer.subjectId] as const,
  ([open, view]) => {
    if (!open) {
      summary.value = null;
      team.value = null;
      return;
    }
    if (view === 'team') {
      if (!team.value) void loadTeam();
    } else {
      void load();
    }
  },
  { immediate: true }
);

const detail = computed(() => summary.value!.detail);

// Reply speed and unanswered questions are not measured (decision 25 Sep).
// Faces saved before that change may still carry such reasons until they are
// recomputed — never show them.
const RETIRED_KINDS = new Set(['unanswered', 'answered_late']);
const reasons = computed(() => detail.value.reasons.filter((r) => !RETIRED_KINDS.has(r.kind)));

const heading = computed(() =>
  isTeam.value
    ? 'Team comms mood'
    : isOwn.value
      ? 'Your comms mood'
      : `${summary.value?.subject.name || drawer.subjectName || 'Order admin'} · comms mood`
);
const subtitle = computed(() =>
  isTeam.value
    ? 'The last 7 days, day by day, then the last 30. Open anyone for the detail — every view is recorded.'
    : isOwn.value
      ? 'How your messages to customers and factories are landing'
      : 'Opened from the team grid — this view is recorded'
);

const PERIOD_WORD: Record<MoodPeriod, string> = {
  day: 'today',
  week: 'in the last 7 days',
  month: 'in the last 30 days',
};

const faceFor = (p: MoodPeriod): MoodLevel => {
  const f = summary.value?.faces;
  if (!f) return 0;
  return (p === 'day' ? f.today : p === 'week' ? f.week : f.month).level;
};

const headline = computed(() =>
  detail.value.level === 0
    ? `No messages ${PERIOD_WORD[drawer.period]}`
    : `${detail.value.label} ${PERIOD_WORD[drawer.period]}`
);

const n = (k: string): number => Number(detail.value.counts?.[k] ?? 0) || 0;
const plural = (x: number, one: string, many = `${one}s`): string => `${x} ${x === 1 ? one : many}`;

const countsLine = computed(() => {
  const bits: string[] = [plural(n('messages'), 'message')];
  if (n('harsh')) bits.push(plural(n('harsh'), 'harsh reply', 'harsh replies'));
  if (n('curt')) bits.push(plural(n('curt'), 'curt reply', 'curt replies'));
  if (n('not_answered')) bits.push(`${plural(n('not_answered'), 'request')} left out`);
  if (bits.length === 1 && n('messages') > 0) bits.push('clear and quick');
  if (n('messages') === 0)
    return `No messages of ${isOwn.value ? 'your' : 'their'} own to a customer or factory — nothing judged`;
  return bits.join(' · ');
});

const nothingYet = computed(
  () => `Nothing to judge — no messages of ${isOwn.value ? 'your' : 'their'} own`
);

/**
 * A measure with no judgement although there ARE messages: say why, never
 * "no messages" (seen 26 Sep — Simab had replies listed right below it).
 */
const notJudged = (key: 'tone' | 'answered' | 'clarity'): string => {
  if (n('messages') === 0) return nothingYet.value;
  const when = PERIOD_WORD[drawer.period];
  if (key === 'answered') return `Not judged — no reply ${when} was clearly answering a question`;
  if (key === 'clarity') return `Not judged — no message ${when} needed a date or next step`;
  return 'Not judged yet';
};

const dims = computed(() => {
  const d = detail.value.dims;
  return [
    {
      key: 'tone',
      label: 'Tone',
      face: d.tone,
      note: !d.tone
        ? notJudged('tone')
        : n('harsh') || n('curt')
          ? `${n('harsh')} harsh · ${n('curt')} curt of ${n('messages')}`
          : 'Courteous throughout',
    },
    {
      key: 'answered',
      label: 'Answered what was asked',
      face: d.answered,
      note: !d.answered
        ? notJudged('answered')
        : n('not_answered') || n('partly_answered')
          ? `${n('not_answered')} left out · ${n('partly_answered')} partly`
          : 'Every request covered',
    },
    {
      key: 'clarity',
      label: 'Clarity',
      face: d.clarity,
      note: !d.clarity
        ? notJudged('clarity')
        : n('vague')
          ? `${plural(n('vague'), 'update')} without a date or next step`
          : 'Dates and next steps given',
    },
    // AB#21991 — only when it was judged: the measure is off until a start
    // date is set, and a person with no ticket-linked orders has nothing here.
    ...(d.threads
      ? [
          {
            key: 'threads',
            label: 'Threads tied to their order',
            face: d.threads,
            note: n('threads_unconfirmed')
              ? `${plural(n('threads_unconfirmed'), 'Zendesk thread')} of ${n('threads_linked')} still to confirm on the order`
              : `${plural(n('threads_linked'), 'linked order')}, every thread says which order it is about`,
          },
        ]
      : []),
  ];
});

/** Same deep link Needs Attention uses: /dashboard?order_id&order_item_id&order_no. */
const orderHref = (r: MoodReason): string | null => {
  if (!r.order_id || !r.order_item_id) return null;
  const q = new URLSearchParams({
    order_id: String(r.order_id),
    order_item_id: String(r.order_item_id),
    ...(r.order_no ? { order_no: String(r.order_no) } : {}),
    na_action: 'view_order',
  });
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  return `${base}/dashboard?${q.toString()}`;
};

const severityFace = (s: number): MoodLevel =>
  s >= 9 ? 5 : (Math.max(1, Math.min(4, s)) as MoodLevel);

const counterpartLabel = (r: MoodReason): string => {
  const who =
    r.counterpart === 'factory' ? 'factory' : r.counterpart === 'team' ? 'team' : 'customer';
  return r.counterpart === 'team' ? 'internal' : `to ${who}`;
};

const when = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}, ${hh}:${mm}`;
};

// --- "this is wrong" ---
const flagged = ref(new Set<number>());
const flagOpen = ref<number | null>(null);
const flagNote = ref('');
const flagBusy = ref(false);

const startFlag = (id: number): void => {
  flagOpen.value = id;
  flagNote.value = '';
};

const sendFlag = async (id: number): Promise<void> => {
  flagBusy.value = true;
  const ok = await flagJudgement(id, flagNote.value.trim() || null);
  flagBusy.value = false;
  if (ok) {
    flagged.value = new Set([...flagged.value, id]);
    flagOpen.value = null;
  }
};
</script>

<style scoped>
.cmd-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--accent);
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}
.cmd-back:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.cmd-back-ic {
  width: 14px;
  height: 14px;
}
.cmd-team-faces {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 22px;
}
.cmd-team-face {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--ink-soft);
}
.cmd-team-face b {
  color: var(--ink);
}
.cmd-body {
  /* The shared Drawer body pads 18px at the top; the team grid's sticky
     header sits over that padding so no row shows above it (AB#21900). */
  --tm-sticky-top: -18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* No action footer: clear the Ask Atlas launcher from the last row. */
  padding-bottom: 96px;
}
.cmd-tabs {
  display: flex;
  gap: 6px;
}
.cmd-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface);
  color: var(--ink-soft);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.cmd-tab-hint {
  font-weight: 400;
  color: var(--ink-mute);
  font-size: 11.5px;
}
.cmd-tab.on {
  border-color: var(--accent);
  background: var(--accent-soft);
  color: var(--ink);
}
.cmd-tab:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.cmd-state,
.cmd-muted {
  color: var(--ink-soft);
  font-size: 13px;
}
.cmd-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface-2);
}
.cmd-hero--l3 {
  border-color: var(--mood-3);
  background: var(--mood-3-soft);
}
.cmd-hero--l2 {
  border-color: var(--mood-2);
  background: var(--mood-2-soft);
}
.cmd-hero--l1 {
  border-color: var(--mood-1);
  background: var(--mood-1-soft);
}
.cmd-hero--l5 {
  background: var(--mood-5-soft);
}
.cmd-hero--l4 {
  background: var(--mood-4-soft);
}
.cmd-hero-t {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.cmd-hero-t b {
  font-size: 16px;
  color: var(--ink);
}
.cmd-hero-t span {
  font-size: 12.5px;
  color: var(--ink-soft);
}
.cmd-h {
  margin: 10px 0 0;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
}
.cmd-dims {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
@media (max-width: 420px) {
  .cmd-dims {
    grid-template-columns: 1fr;
  }
}
.cmd-dim {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid var(--border-soft);
  border-radius: 10px;
}
.cmd-dim b {
  display: block;
  font-size: 12.5px;
  color: var(--ink);
}
.cmd-dim small {
  display: block;
  font-size: 11.5px;
  color: var(--ink-soft);
}
.cmd-ev {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
}
.cmd-ev--good {
  border-style: dashed;
}
.cmd-ev-t {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink);
}
.cmd-ev-q {
  display: block;
  padding-left: 10px;
  border-left: 2px solid var(--border);
  font-size: 13px;
  color: var(--ink-soft);
  overflow-wrap: anywhere;
}
.cmd-ev-m {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 11.5px;
  color: var(--ink-mute);
}
.cmd-ev-order-plain {
  color: var(--ink-soft);
  font-weight: 700;
}
.cmd-ev-order {
  color: var(--accent);
  font-weight: 700;
}
.cmd-ev-why {
  margin: 0;
  font-size: 12.5px;
  color: var(--ink-soft);
}
.cmd-ev-sug {
  display: flex;
  gap: 6px;
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--accent-softer);
  font-size: 12.5px;
  color: var(--ink);
}
.cmd-ev-sug-ic {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  color: var(--accent);
}
.cmd-flag {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cmd-flag-btn {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ink-mute);
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}
.cmd-flag-btn:hover {
  color: var(--accent);
}
.cmd-flag-lab {
  font-size: 12px;
  color: var(--ink-soft);
}
.cmd-flag-in {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 12.5px;
  resize: vertical;
}
.cmd-flag-row {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.cmd-flag-done {
  font-size: 12px;
  color: var(--success);
}
.cmd-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 12.5px;
  color: var(--ink-soft);
}
.cmd-legend li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.cmd-legend b {
  color: var(--ink);
}
.cmd-foot {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: var(--ink-mute);
}
</style>
